import { ThemedSelect } from '@components/common/ThemedSelect';

// The page controls — and ONLY the controls.
//
// The defect this replaces: `@centraoneplus/ui`'s <Pagination> brings its own
// full-width bar (`border-t`, `bg-white`, `py-3`). Dropped inside a card's foot
// band — which is where a pager always goes — you get a white box inside a
// tinted bar, two borders, two paddings, and a footer twice the height it
// needs. So this renders an inline cluster with no ground of its own, and the
// band it sits in stays the band.
//
// The same markup existed privately in BacktrackPage as `ArchiveFooter`, which
// is where the page-number algorithm and the callback shape come from; this is
// that component with its bar removed so more than one screen can use it.

const MONO = 'co-mono';

/** 7 visible, ellipses either side — unchanged from the component this replaces. */
export function pageWindow(currentPage: number, totalPages: number): (number | 'ellipsis')[] {
  const pages: (number | 'ellipsis')[] = [];
  const maxVisible = 7;
  if (totalPages <= maxVisible) {
    for (let i = 1; i <= totalPages; i += 1) pages.push(i);
    return pages;
  }
  pages.push(1);
  if (currentPage > 3) pages.push('ellipsis');
  for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i += 1) {
    pages.push(i);
  }
  if (currentPage < totalPages - 2) pages.push('ellipsis');
  pages.push(totalPages);
  return pages;
}

export interface PagerProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  /** Omit to hide the page-size control entirely. */
  onPageSizeChange?: (size: number) => void;
  pageSizeOptions?: number[];
  /**
   * `1–20 of 122`. Off by default: most foot bands already state what is on
   * screen in their own words, and two counts in one bar disagree the moment
   * one of them is phrased differently.
   */
  showRange?: boolean;
  /** Needed only when two pagers share a screen (an id must stay unique). */
  id?: string;
}

const PILL =
  'flex h-[26px] min-w-[26px] items-center justify-center rounded-full px-[9px] ' +
  'text-[11.5px] transition-colors';

const STEP =
  `${PILL} text-[var(--co-ink-2)] hover:bg-[var(--co-brass-wash)] hover:text-[var(--co-brass-ink)] ` +
  'disabled:cursor-not-allowed disabled:opacity-35 disabled:hover:bg-transparent';

export function Pager({
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 50, 100],
  showRange = false,
  id = 'pager',
}: PagerProps) {
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  return (
    <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-2">
      {showRange && (
        <span
          className={`${MONO} whitespace-nowrap text-[10.5px] uppercase tracking-[.1em] text-[var(--co-ink-3)]`}
        >
          {startItem}–{endItem} of {totalItems}
        </span>
      )}

      {onPageSizeChange && (
        <div className="flex items-center gap-[6px]">
          <label
            htmlFor={`${id}-page-size`}
            className={`${MONO} text-[9.5px] font-semibold uppercase tracking-[.16em] text-[var(--co-ink-3)]`}
          >
            Show
          </label>
          <ThemedSelect
            id={`${id}-page-size`}
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="h-[26px] rounded-full border border-[var(--co-line-strong)] bg-[var(--co-raised)] pl-[10px] pr-[26px] text-[11.5px] text-[var(--co-ink)] focus:border-[var(--co-brass)] focus:outline-none"
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </ThemedSelect>
        </div>
      )}

      {/* One rounded track holding the steps and the pages, the same shape the
          tab strips use. The pages were separate square boxes with shared
          borders, which reads as a toolbar rather than as one control. */}
      <nav
        aria-label="Pagination"
        className="flex items-center gap-[2px] rounded-full border border-[var(--co-line)] bg-[var(--co-raised)] p-[3px]"
      >
        <button
          type="button"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage <= 1}
          aria-label="Previous page"
          className={STEP}
        >
          ‹
        </button>

        {pageWindow(currentPage, totalPages).map((p, i) =>
          p === 'ellipsis' ? (
            <span
              key={`ellipsis-${i}`}
              aria-hidden="true"
              className={`${MONO} px-[3px] text-[11.5px] text-[var(--co-ink-4)]`}
            >
              …
            </span>
          ) : (
            <button
              key={p}
              type="button"
              onClick={() => onPageChange(p)}
              aria-current={p === currentPage ? 'page' : undefined}
              aria-label={`Page ${p}`}
              className={`${MONO} ${PILL} font-semibold`}
              style={
                p === currentPage
                  ? { background: 'var(--co-brass-ink)', color: 'var(--co-brass-on)' }
                  : { color: 'var(--co-ink-2)' }
              }
            >
              {p}
            </button>
          ),
        )}

        <button
          type="button"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages}
          aria-label="Next page"
          className={STEP}
        >
          ›
        </button>
      </nav>
    </div>
  );
}
